import { useMutation } from '@tanstack/react-query';
import styled from 'styled-components';

import { ActionMenu, Tooltip } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { Button, IconButton } from '@creditornot/cb-button';
import { MobileSearchButton } from 'common/components/cookbook/Search/MobileSearchButton';
import { Stack } from 'common/components/cookbook/Stack';
import { useFeatureEnabled } from 'common/feature-flag/hooks/useFeatureEnabled';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useMatchMedia } from 'common/hooks/useMatchMedia';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useSetSearch } from 'common/hooks/useSetSearch';
import { useToast } from 'common/hooks/useToast';
import { dateToISO } from 'common/utils/dates/dateUtils';
import { downloadBlob } from 'common/utils/downloadUtils';

import { vendorsLayoutRoute } from '..';
import { VendorsSearchInput } from './VendorsSearchInput';
import {
  downloadDeliveryCalendar,
  downloadDeliverySchedules,
  downloadOrderingConditions,
} from '../api';
import { vendorCommunicationRouteApi } from '../routeApi';
import { vendorsListRoute } from '../VendorsView';

const NavBarControls = styled(Stack)`
  flex-shrink: 0;
`;

const NavBarButton = styled(Button)`
  flex-shrink: 0;
`;

function VendorSettingsButton() {
  const f = useFormatMessage();
  const navigate = vendorsListRoute.useNavigate();
  const navigateToVendorCommunication = vendorCommunicationRouteApi.useNavigate();

  const { addErrorToast } = useToast();

  const countryCodes = useSelectedCountryCodes();

  const isMobile = useMatchMedia('maxMd');
  const isClawbacksEnabled = useFeatureEnabled('clawback-requests', countryCodes);

  const disabled = countryCodes.length !== 1;
  const disabledTooltip = disabled ? f('vendors.select_single_country') : undefined;

  const { mutate: mutateOrderingConditionsDownload } = useMutation({
    mutationFn: () => downloadOrderingConditions(countryCodes),
    onSuccess: (data) => {
      const isoDate = dateToISO(new Date());
      const filename = `${isoDate}-ordering-conditions-${countryCodes[0]}`;
      downloadBlob(data, filename);
    },
    onError: addErrorToast,
  });

  const { mutate: mutateDeliverySchedulesDownload } = useMutation({
    mutationFn: () => downloadDeliverySchedules(countryCodes),
    onSuccess: (data) => {
      const isoDate = dateToISO(new Date());
      const filename = `${isoDate}-delivery-schedules-${countryCodes[0]}`;
      downloadBlob(data, filename);
    },
    onError: addErrorToast,
  });

  const { mutate: mutateDeliveryCalendarDownload } = useMutation({
    mutationFn: () => downloadDeliveryCalendar(countryCodes),
    onSuccess: (data) => {
      const isoDate = dateToISO(new Date());
      const filename = `${isoDate}-delivery-calendars-${countryCodes[0]}`;
      downloadBlob(data, filename);
    },
    onError: addErrorToast,
  });

  const button = isMobile ? (
    <IconButton
      aria-label={f('vendors.vendor_settings')}
      disabled={disabled}
      icon={icons.gearFilled}
      size="small"
      variant="brand"
    />
  ) : (
    <NavBarButton disabled={disabled} size="small" variant="secondary">
      {f('vendors.vendor_settings')}
    </NavBarButton>
  );

  return (
    <ActionMenu.Root
      trigger={disabled ? <Tooltip content={disabledTooltip}>{button}</Tooltip> : button}
    >
      {isClawbacksEnabled && (
        <ActionMenu.Item
          label={f('procurement.vendor_communication.title')}
          onClick={() => navigateToVendorCommunication({})}
        />
      )}
      <ActionMenu.Submenu label={f('vendors.ordering_conditions')}>
        <ActionMenu.Item
          label={f('common.download_csv')}
          onClick={() => mutateOrderingConditionsDownload()}
        />
        <ActionMenu.Item
          label={f('common.upload_csv')}
          onClick={() =>
            navigate({ search: (prev) => ({ ...prev, uploadOrderingConditionsModalOpen: true }) })
          }
        />
      </ActionMenu.Submenu>
      <ActionMenu.Submenu label={f('vendors.delivery_schedules')}>
        <ActionMenu.Item
          label={f('common.download_csv')}
          onClick={() => mutateDeliverySchedulesDownload()}
        />
        <ActionMenu.Item
          label={f('common.upload_csv')}
          onClick={() =>
            navigate({
              search: (prev) => ({ ...prev, uploadDeliveryScheduleModalOpen: true }),
            })
          }
        />
      </ActionMenu.Submenu>
      <ActionMenu.Submenu label={f('vendors.delivery_calendar')}>
        <ActionMenu.Item
          label={f('common.download_csv')}
          onClick={() => mutateDeliveryCalendarDownload()}
        />
      </ActionMenu.Submenu>
    </ActionMenu.Root>
  );
}

export function VendorsNavBarControls() {
  const f = useFormatMessage();

  const isMobile = useMatchMedia('maxMd');

  const setSearch = useSetSearch(vendorsLayoutRoute);

  const openCreateModal = () => setSearch({ createModalOpen: true });

  return (
    <NavBarControls gap="regularXlarge" direction="row">
      {!isMobile && <VendorsSearchInput />}
      {isMobile ? (
        <>
          <MobileSearchButton />
          <VendorSettingsButton />
          <IconButton
            icon={icons.plus}
            size="small"
            variant="brand"
            onClick={openCreateModal}
            aria-label={f('vendors.add_a_vendor')}
            data-test-id="create-vendor-button"
          />
        </>
      ) : (
        <>
          <VendorSettingsButton />
          <NavBarButton
            size="small"
            variant="primary"
            onClick={openCreateModal}
            data-test-id="create-vendor-button"
            icon={icons.plus}
          >
            {f('vendors.add_a_vendor')}
          </NavBarButton>
        </>
      )}
    </NavBarControls>
  );
}
